import React, { useEffect, useState, useMemo } from 'react';
import { Container, Typography, Box, Card, Grid, IconButton, useTheme, alpha, Button, Stack, Avatar } from '@mui/material';
import { fetchRestaurantInfo } from './api';
import config from './config';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useTranslation } from 'react-i18next';

function Support() {
  const theme = useTheme();
  const [info, setInfo] = useState(null);
  const [bgImage, setBgImage] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchRestaurantInfo().then(setInfo).catch(console.error);
    
    // Fetch slider images for background
    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const image = data.find(img => img.IsActive);
          if (image) {
            const imgPath = image.ImagePath.startsWith('http') 
              ? image.ImagePath 
              : `${config.SITE_BASE_URL}${image.ImagePath.startsWith('/') ? '' : '/'}${image.ImagePath}`;
            setBgImage(imgPath);
          }
        }
      })
      .catch(console.error);
  }, []);

  const additionalPhones = useMemo(() => {
    if (!info?.AdditionalPhones) return [];
    try {
      return typeof info.AdditionalPhones === 'string' 
        ? JSON.parse(info.AdditionalPhones) 
        : info.AdditionalPhones;
    } catch (e) {
      return [];
    }
  }, [info]);

  const handleContact = (type, value) => {
    switch (type) {
        case 'whatsapp':
            window.open(`https://wa.me/${value.replace(/[^0-9]/g, '')}`, '_blank');
            break;
        case 'phone':
            window.location.href = `tel:${value}`;
            break;
        case 'email':
            window.location.href = `mailto:${value}`;
            break;
        case 'location':
            window.open(`https://maps.google.com/?q=${value}`, '_blank');
            break;
        case 'social':
            window.open(value, '_blank');
            break;
    }
  };

  const ContactCard = ({ icon, title, subtitle, action, color, onClick }) => (
    <Card 
      onClick={onClick}
      sx={{ 
        p: 3, 
        height: '100%',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        textAlign: 'center', 
        cursor: 'pointer', 
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        '&:hover': { 
          transform: 'translateY(-8px)',
          boxShadow: `0 12px 30px ${alpha(color || theme.palette.primary.main, 0.15)}`,
          borderColor: alpha(color || theme.palette.primary.main, 0.3)
        }
      }}
    >
      <Box sx={{ 
        bgcolor: alpha(color || theme.palette.primary.main, 0.1), 
        color: color || theme.palette.primary.main,
        width: 70, 
        height: 70, 
        borderRadius: '50%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        mb: 2.5,
        transition: 'transform 0.3s ease',
        '.MuiCard-root:hover &': {
            transform: 'scale(1.1) rotate(5deg)'
        }
      }}>
        {React.cloneElement(icon, { fontSize: 'large' })}
      </Box>
      <Typography variant="h6" fontWeight="800" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
        {subtitle}
      </Typography>
      <Button 
        variant="outlined" 
        size="small" 
        sx={{ 
            borderRadius: 20, 
            px: 3,
            borderColor: alpha(color || theme.palette.primary.main, 0.5),
            color: color || theme.palette.primary.main,
            '&:hover': {
                bgcolor: color || theme.palette.primary.main,
                color: '#fff',
                borderColor: color || theme.palette.primary.main
            }
        }}
      >
        {action}
      </Button>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative', 
        height: 300, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'white',
        mb: 6,
        overflow: 'hidden'
      }}>
        <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: bgImage ? `url(${bgImage})` : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(4px)',
            transform: 'scale(1.1)'
        }} />
        <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.6)',
            zIndex: 1
        }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <SupportAgentIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
            <Typography variant="h3" fontWeight="900" sx={{ mb: 1 }}>
                {t('account.helpCenter', 'Help Center')}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                {t('support.howCanWeHelp', 'We are here to help you')}
            </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={3}>
            {/* Main Phone (WhatsApp) */}
            {info?.PhoneNumber && (
                <Grid item xs={12} sm={6} md={4}>
                    <ContactCard 
                        icon={<WhatsAppIcon />}
                        title={t('support.whatsapp', 'WhatsApp')}
                        subtitle={info.PhoneNumber.replace(/[^0-9]/g, '').slice(-9)}
                        action={t('common.chat', 'Chat')}
                        color="#25D366"
                        onClick={() => handleContact('whatsapp', info.PhoneNumber)}
                    />
                </Grid>
            )}

            {/* Main Contact Channels */}
            {info?.Email && (
                <Grid item xs={12} sm={6} md={4}>
                    <ContactCard 
                        icon={<EmailIcon />}
                        title={t('support.emailUs', 'Email Us')}
                        subtitle={info.Email}
                        action={t('common.send', 'Send Email')}
                        color={theme.palette.error.main}
                        onClick={() => handleContact('email', info.Email)}
                    />
                </Grid>
            )}
            
            {info?.Address && (
                <Grid item xs={12} sm={6} md={4}>
                    <ContactCard 
                        icon={<LocationOnIcon />}
                        title={t('footer.address', 'Visit Us')}
                        subtitle={info.Address}
                        action={t('common.viewMap', 'View Map')}
                        color={theme.palette.warning.main}
                        onClick={() => handleContact('location', info.Address)}
                    />
                </Grid>
            )}

            {/* Additional Phones */}
            {additionalPhones.map((phone, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                    <ContactCard 
                        icon={<PhoneIcon />}
                        title={phone.title}
                        subtitle={phone.number ? phone.number.replace(/[^0-9]/g, '').slice(-9) : ''}
                        action={t('support.callUs', 'Call Now')}
                        color={theme.palette.success.main}
                        onClick={() => handleContact('phone', phone.number)}
                    />
                </Grid>
            ))}

            {/* Social Media */}
            {info?.FacebookUrl && (
                <Grid item xs={12} sm={6} md={3}>
                    <ContactCard 
                        icon={<FacebookIcon />}
                        title="Facebook"
                        subtitle={t('common.followUs', 'Follow us')}
                        action={t('common.visit', 'Visit')}
                        color="#1877F2"
                        onClick={() => handleContact('social', info.FacebookUrl)}
                    />
                </Grid>
            )}
            {info?.InstagramUrl && (
                <Grid item xs={12} sm={6} md={3}>
                    <ContactCard 
                        icon={<InstagramIcon />}
                        title="Instagram"
                        subtitle={t('common.followUs', 'Follow us')}
                        action={t('common.visit', 'Visit')}
                        color="#E4405F"
                        onClick={() => handleContact('social', info.InstagramUrl)}
                    />
                </Grid>
            )}
            {info?.TwitterUrl && (
                <Grid item xs={12} sm={6} md={3}>
                    <ContactCard 
                        icon={<TwitterIcon />}
                        title="Twitter"
                        subtitle={t('common.followUs', 'Follow us')}
                        action={t('common.visit', 'Visit')}
                        color="#1DA1F2"
                        onClick={() => handleContact('social', info.TwitterUrl)}
                    />
                </Grid>
            )}
        </Grid>
      </Container>
    </Box>
  );
}

export default Support;
